const getResponseData = (response) => {
  if (!response || !response.data) return null;
  return response.data.res || response.data;
};

export const getEventIdFromResponse = (response) => {
  const data = getResponseData(response);
  return data && (data._id || data.id) ? String(data._id || data.id) : '';
};

const getAuthConfig = () => ({
  headers: {
    'x-auth-token': localStorage.getItem('bde_token')
  }
});

const confirmUserAssociationAction = async (vm, options) => {
  const {
    title,
    description,
    users,
    keywordLabel,
    confirmButtonText,
    emptyWarning,
    endpoint,
    eventId,
    resultCountField,
    successText
  } = options;
  if (!Array.isArray(users) || !users.length) return;

  let selectedUserIds = users.map(user => String(user._id));
  const h = vm.$createElement;
  const message = h('div', { class: 'event-user-link-confirm' }, [
    h('p', { class: 'event-user-link-title' }, description),
    h('div', { class: 'event-user-link-list' }, users.map(user => {
      const userId = String(user._id);
      const keywords = keywordLabel === '匹配关键词'
        ? (user.matchedKeywords || [])
        : (user.brandKeywords || []);
      return h('label', {
        key: userId,
        class: 'event-user-link-option'
      }, [
        h('input', {
          class: 'event-user-link-checkbox',
          attrs: {
            type: 'checkbox',
            checked: true
          },
          on: {
            change: (event) => {
              if (event.target.checked) {
                selectedUserIds = Array.from(new Set(selectedUserIds.concat(userId)));
              } else {
                selectedUserIds = selectedUserIds.filter(id => id !== userId);
              }
            }
          }
        }),
        h('div', { class: 'event-user-link-details' }, [
          h('div', { class: 'event-user-link-field' }, [
            h('span', { class: 'event-user-link-label' }, '账号：'),
            h('span', { class: 'event-user-link-name' }, user.username)
          ]),
          h('div', { class: 'event-user-link-field' }, [
            h('span', { class: 'event-user-link-label' }, `${keywordLabel}：`),
            h('span', { class: 'event-user-link-keywords' }, keywords.join('、') || '无')
          ])
        ])
      ]);
    }))
  ]);

  await vm.$msgbox({
    title,
    message,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: '暂不处理',
    customClass: 'event-user-link-message-box',
    beforeClose: async (action, instance, done) => {
      if (action !== 'confirm') {
        done();
        return;
      }
      if (!selectedUserIds.length) {
        vm.$message.warning(emptyWarning);
        return;
      }

      instance.confirmButtonLoading = true;
      try {
        const response = await vm.$axios.post(endpoint, JSON.stringify({
          eventId,
          userIds: selectedUserIds
        }), getAuthConfig());
        const result = getResponseData(response) || {};
        vm.$message.success(`${successText} ${result[resultCountField] || 0} 个赛事方账号`);
        done();
      } catch (err) {
        vm.$message.error(err.response?.data?.message || `${successText}赛事方账号失败`);
      } finally {
        instance.confirmButtonLoading = false;
      }
    }
  }).catch(() => {});
};

const fetchEventUserAssociationReview = async (vm, eventId) => {
  const response = await vm.$axios.post(
    '/admin-users/review-event-user-associations',
    JSON.stringify({ eventId }),
    getAuthConfig()
  );
  return getResponseData(response) || {};
};

export const getMatchedLinkedUserIds = async (vm, eventId) => {
  const normalizedEventId = String(eventId || '').trim();
  if (!vm || !normalizedEventId) return [];
  try {
    const result = await fetchEventUserAssociationReview(vm, normalizedEventId);
    return (result.matchedLinked || []).map(user => String(user._id));
  } catch (err) {
    console.warn('读取赛事方账号原关联状态失败', err);
    return [];
  }
};

export const confirmLinkEventUsers = async (vm, eventInfo = {}) => {
  const eventId = String(eventInfo.eventId || '').trim();
  if (!vm || !eventId) return;

  let matchedUsers = [];
  try {
    const response = await vm.$axios.post(
      '/admin-users/match-event-users',
      JSON.stringify({ eventId }),
      getAuthConfig()
    );
    matchedUsers = getResponseData(response) || [];
  } catch (err) {
    // 不阻断新增/复制赛事本身；无用户更新权限时只跳过自动关联检查。
    console.warn('匹配赛事方账号失败', err);
    return;
  }

  if (!Array.isArray(matchedUsers) || matchedUsers.length === 0) return;

  const eventName = eventInfo.eventName || '该赛事';
  await confirmUserAssociationAction(vm, {
    title: '关联赛事方账号',
    description: `“${eventName}”匹配到以下赛事方账号，是否将赛事ID自动关联给他们？`,
    users: matchedUsers,
    keywordLabel: '匹配关键词',
    confirmButtonText: '确认关联',
    emptyWarning: '请至少选择一个账号，或点击暂不处理',
    endpoint: '/admin-users/link-event-users',
    eventId,
    resultCountField: 'linkedCount',
    successText: '已关联'
  });
};

export const reviewEventUserAssociations = async (vm, eventInfo = {}) => {
  const eventId = String(eventInfo.eventId || '').trim();
  if (!vm || !eventId) return;

  let reviewResult;
  try {
    reviewResult = await fetchEventUserAssociationReview(vm, eventId);
  } catch (err) {
    console.warn('检查赛事方账号关联失败', err);
    return;
  }

  const eventName = eventInfo.eventName || '该赛事';
  const previouslyMatchedUserIds = new Set(
    (eventInfo.previouslyMatchedUserIds || []).map(id => String(id))
  );
  const usersToUnlink = (reviewResult.toUnlink || []).filter(user => {
    return previouslyMatchedUserIds.has(String(user._id));
  });
  await confirmUserAssociationAction(vm, {
    title: '关联赛事方账号',
    description: `“${eventName}”修改后匹配到以下赛事方账号，是否将赛事ID关联给他们？`,
    users: reviewResult.toLink,
    keywordLabel: '匹配关键词',
    confirmButtonText: '确认关联',
    emptyWarning: '请至少选择一个账号，或点击暂不处理',
    endpoint: '/admin-users/link-event-users',
    eventId,
    resultCountField: 'linkedCount',
    successText: '已关联'
  });

  await confirmUserAssociationAction(vm, {
    title: '解绑赛事方账号',
    description: `“${eventName}”修改后已不再匹配以下账号的品牌关键词，是否解除赛事关联？`,
    users: usersToUnlink,
    keywordLabel: '账号品牌关键词',
    confirmButtonText: '确认解绑',
    emptyWarning: '请至少选择一个账号，或点击暂不处理',
    endpoint: '/admin-users/unlink-event-users',
    eventId,
    resultCountField: 'unlinkedCount',
    successText: '已解绑'
  });
};
